import * as Sentry from '@sentry/vue'
import PricingApi from '@/api/pricing'
import PromoApi from '@/api/promo'
import utils from '@/util'
import router from '@/router'
import InvoiceApi from '@/api/invoice'
import mixpanelUtils from '@/utils/mixpanel.utils'

const TOAST_ERROR = 'Something went wrong, please check your connection or contact us for support'

const createCheckoutSessionAction = async ({ commit, dispatch }, payload) => {
  try {
    commit('isCreatingCheckoutSession', true)
    const res = await PricingApi.createStripeCheckoutSession({
      planType: payload.plan,
      quantity: payload.quantity,
      origin: window.location.href,
      trackingData: mixpanelUtils.getProperties(),
    })
    if (res.sessionUrl) {
      window.location.href = res.sessionUrl
    }
  } catch (e) {
    if (e.body && e.body.message) {
      commit('createCheckoutSessionErrorMutation', e.body.message)
    }
    dispatch('hookBulkPricingAction', {
      planType: payload.plan,
      numOfLicenses: payload.quantity,
      email: payload.email,
      requirement: 'This was initiated because of failure during checkout session creation.',
    })
    throw e
  } finally {
    commit('isCreatingCheckoutSession', false)
  }
}
const createCheckoutSessionForPersonPlan = async ({ commit, dispatch }, payload) => {
  try {
    commit('isCreatingCustomerPortal', true)

    const { plan: planType, promotionCode, query } = payload
    commit('loadingPlan', planType)

    const data = await PricingApi.createCheckoutSessionForPersonPlan({
      planType,
      promotionCode,
      query,
      trackingData: mixpanelUtils.getProperties(),
    })
    if (data && data.sessionUrl) {
      window.location.href = data.sessionUrl
      commit('isCreatingCustomerPortal', false)
      commit('loadingPlan', null)
    }
  } catch (e) {
    Sentry.captureException(e)
    utils.showToastError(
      TOAST_ERROR,
    )
    dispatch('')
    commit('isCreatingCustomerPortal', false)
    commit('loadingPlan', null)
    return e
  }
}
const createCustomerPortalUpgradeFlowAction = async ({ commit }, payload) => {
  try {
    commit('isCreatingCustomerPortal', true)
    commit('loadingPlan', payload.plan)
    const data = await PricingApi.createCustomerPortalUpgradeFlow(payload)
    commit('isCreatingCustomerPortal', false)
    if (data && data.url) {
      window.location.href = data.url
      commit('loadingPlan', null)
    }
  } catch (e) {
    Sentry.captureException(e)
    utils.showToastError(
      TOAST_ERROR,
    )
    commit('isCreatingCustomerPortal', false)
    commit('loadingPlan', null)
    return e
  }
}
const createCustomerPortalAction = async ({ commit }, payload) => {
  try {
    commit('isCreatingCustomerPortal', true)
    const data = await PricingApi.createCustomerPortal({
      pathname: window.location.pathname,
      isUpdatingPlan: payload ? payload.isUpdatingPlan : false,
    })
    commit('isCreatingCustomerPortal', false)
    if (data && data.url) {
      window.location.href = data.url
    }
  } catch (e) {
    Sentry.captureException(e)
    utils.showToastError(
      TOAST_ERROR,
    )
    commit('isCreatingCustomerPortal', false)
  }
}
const hookBulkPricingAction = async ({ commit }, payload) => {
  try {
    return PricingApi.hookBulkPricing(payload)
  } catch (e) {
    Sentry.captureException(e)
    commit('hookBulkPricingErrorMutation', e)
  }
}
const getPromotionCode = async ({ commit }, payload) => {
  const { promotionCode } = await PromoApi.checkPromotion({
    promo: payload,
  })
  commit('setPromotionCode', promotionCode)
}

const setIsValidPromoCodeAction = async ({ commit }, payload) => {
  commit('setIsValidPromoCode', payload)
}
const updateRemoveCardPurposeAction = ({ commit }, payload) => {
  commit('setRemoveCardPurpose', payload)
}

const requestAdditionalLicensePopupAction = async ({ commit }, payload) => {
  commit('isShowingRequestAdditionalLicensePopup', payload)
}

const goToBulkLicensePageAction = () => {
  router.push({
    name: 'UserPlan',
    hash: '#bulk-pricing',
  })
}
const trackNewPurchaseAction = (
  { state }, payload,
) => {
  const { currentUser, plan } = state

  if (payload.query) {
    setTimeout(async () => {
      const res = await InvoiceApi.getInvoices()

      if (res.invoices && res.invoices.length === 0) {
        return
      }
      const invoice = res.invoices[0]

      const { source, type } = payload.query

      let coupon = ''
      if (invoice.coupon) {
        coupon = invoice.coupon.name
      }
      let discount = 0
      if (invoice.lines && invoice.lines.length > 0) {
        discount = invoice.lines[invoice.lines.length - 1].discountAmountRaw
      }
      const payment_method = invoice.paymentMethod.brand === 'visa' ? 'Stripe > Cards' : 'Stripe > iDEAL with SEPA'
      const params = {
        currency: invoice.currency,
        ahaslides_user_id: currentUser.id,
        transaction_id: invoice.invoiceId,
        value: invoice.amountPaidRaw,
        coupon,
        tax: 0,
        item_name: plan.name,
        discount,
        payment_method,
        item_category: plan.group,
        item_category2: plan.billingCycle,
        item_category3: type,
        item_variant: source === 'landing_site' ? 'Landing site' : 'Presenter app',
      }
      utils.trackGA4AndMixpanel('purchase', {
        ...params,
      })
      router.replace({ query: null })
    }, 1000)
  }
}

const hookCustomRequestAction = async ({ commit }, payload) => {
  try {
    return PricingApi.hookCustomRequest(payload)
  } catch (e) {
    Sentry.captureException(e)
    commit('hookCustomRequestError', e)
  }
}

const submitEnterpriseInquiriesFormAction = async (_, payload) => {
  try {
    return PricingApi.submitEnterpriseInquiriesForm(payload)
  } catch (e) {
    Sentry.captureException(e)
  }
}

export default {
  createCheckoutSessionAction,
  createCheckoutSessionForPersonPlan,
  hookBulkPricingAction,
  updateRemoveCardPurposeAction,
  createCustomerPortalAction,
  createCustomerPortalUpgradeFlowAction,
  requestAdditionalLicensePopupAction,
  goToBulkLicensePageAction,
  trackNewPurchaseAction,
  hookCustomRequestAction,
  getPromotionCode,
  setIsValidPromoCodeAction,
  submitEnterpriseInquiriesFormAction,
}
